<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import {
    UiButton,
    UiCheckbox,
    UiForm,
    UiFormField,
    UiInfoBox,
    UiInputField,
    UiSelect,
    UiTextarea,
} from "@shopexperts/shared";
import BanRuleTermsField from "./BanRuleTermsField.vue";
import {
    MATCH_MODE_OPTIONS,
    banRuleFormSchema,
    type BanRuleFormValues,
} from "@/app/composables/ban-list/banListForms";
import { describeMatchMode } from "@/app/composables/ban-list/banListFormatters";

const props = defineProps<{
    isEditing?: boolean;
    isSubmitting?: boolean;
    typeOptions: readonly { label: string; value: string }[];
    serverErrors?: Record<string, string | undefined>;
}>();

const emit = defineEmits<{
    (e: "submit", values: BanRuleFormValues): void;
    (e: "cancel"): void;
    (e: "change"): void;
}>();

const model = defineModel<BanRuleFormValues>({ required: true });

const formRef = useTemplateRef<{ resetForm: () => void; forceClear: () => void }>("formRef");

const matchModeHint = computed(() => describeMatchMode(model.value.match_mode));

// `type` selects the server-side matcher, and `match_mode` is the project_text
// matcher's own knob — it means nothing to any other type.
const showsMatchMode = computed(() => model.value.type === "project_text");

defineExpose({
    resetForm: () => {
        formRef.value?.resetForm();
        formRef.value?.forceClear();
    },
});

function onSubmit(values: BanRuleFormValues) {
    emit("submit", values);
}
</script>

<template>
    <UiForm
        ref="formRef"
        :schema="banRuleFormSchema"
        :initial-values="model"
        v-model="model"
        :server-errors="props.serverErrors"
        scroll-to-first-error
        @update:model-value="emit('change')"
        @submit="(values: BanRuleFormValues) => onSubmit(values)"
    >
        <div class="flex flex-col gap-5">
            <UiFormField name="name" label="Rule name">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="name"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        placeholder="Link spam"
                        hint="Shown on every ban this rule produces."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="description" label="Description">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiTextarea
                        :id="id"
                        name="description"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        :rows="3"
                        placeholder="Guest-post link farming."
                        hint="Optional. Why this rule exists — for the next admin reading it."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="terms" label="Terms">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <BanRuleTermsField
                        :id="id"
                        :model-value="(modelValue as string[]) ?? []"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <UiFormField name="type" label="Rule type">
                    <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                        <UiSelect
                            :id="id"
                            name="type"
                            :options="(props.typeOptions as { label: string; value: string }[])"
                            :model-value="modelValue as string"
                            :invalid="invalid"
                            :error="error"
                            :disabled="props.isSubmitting"
                            placeholder="Select a type"
                            hint="Selects the matcher that evaluates the rule."
                            @update:model-value="onUpdateModelValue"
                            @validate="onValidate"
                        />
                    </template>
                </UiFormField>

                <UiFormField v-if="showsMatchMode" name="match_mode" label="Match mode">
                    <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                        <UiSelect
                            :id="id"
                            name="match_mode"
                            :options="(MATCH_MODE_OPTIONS as unknown as { label: string; value: string }[])"
                            :model-value="modelValue as string"
                            :invalid="invalid"
                            :error="error"
                            :disabled="props.isSubmitting"
                            placeholder="Whole word"
                            :hint="matchModeHint"
                            @update:model-value="onUpdateModelValue"
                            @validate="onValidate"
                        />
                    </template>
                </UiFormField>
            </div>

            <UiFormField name="is_active" type="checkbox">
                <template #default="{ modelValue, onUpdateModelValue, onValidate }">
                    <UiCheckbox
                        name="is_active"
                        :model-value="Boolean(modelValue)"
                        :disabled="props.isSubmitting"
                        label="Rule is active"
                        description="Inactive rules keep their record and their bans, but stop screening submissions."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiInfoBox v-if="props.isEditing" variant="info">
                Saving replaces the whole term list. Bans already recorded against this rule are not affected.
            </UiInfoBox>

            <div class="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <UiButton
                    variant="outline"
                    label="Cancel"
                    :disabled="props.isSubmitting"
                    @click="emit('cancel')"
                />
                <UiButton
                    type="submit"
                    variant="primary"
                    :label="props.isEditing ? 'Save Changes' : 'Create Rule'"
                    :disabled="props.isSubmitting"
                />
            </div>
        </div>
    </UiForm>
</template>
